import React, {useCallback, useEffect, useState} from "react";
import {Grid, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {Application, applicationSchema, translationsOfRolesComplete} from "./Application";
import {makeRequired} from "../../shared/components/forms/formUtils";
import {handleFormError, RhfForm} from "../../shared/components/forms/RhfForm";
import {FormHeader} from "../../shared/components/forms/FormHeader";
import {yupResolver} from "@hookform/resolvers/yup";
import Typography from "@mui/material/Typography";
import {MuiRhfSelectField} from "../../shared/components/forms/MuiRhfSelectField";
import {ApplicationRolesComponent} from "./ApplicationRolesComponent";
import {useAccount} from "../users/AccountProvider";
import {mayEditApplication} from "./permissions";
import {Role} from "../users/Role";
import {ApplicationSubPageContentContainer} from "./ApplicationSubPageContentContainer";

export function ApplicationRolesSubPage(props: {
  application: Application,
  handleSave: (application: Application) => Promise<Application>,
}) {
  const {application, handleSave} = props;
  const {t} = useTranslation();
  const theme = useTheme();
  const account = useAccount();
  const [formReadonly, setFormReadonly] = useState<boolean>(!!application.id);
  const methods = useForm<Application>({
    mode: 'onBlur',
    defaultValues: application,
    resolver: yupResolver(applicationSchema(t))
  });
  const {register, control, reset, setError} = methods;
  const rolesWatch: Role[] = useWatch<Application, "roles">({name: "roles", control: control});

  useEffect(() => {
    register('id');
  }, [register]);

  const saveApplication = useCallback((application: Application) => {
    handleSave(application).then(() => setFormReadonly(true), (error) => {
      handleFormError(error, setError, t);
    });
  }, [handleSave, setError, t])

  const stdFieldProps = {
    variant: "outlined" as "outlined",
    disabled: formReadonly,
  }
  const required = makeRequired(applicationSchema(t));

  return (
    <ApplicationSubPageContentContainer>
      <FormProvider {...methods} >
        <RhfForm id={'application-form'} onSubmit={saveApplication}>
          <FormHeader title={t('ApplicationPage.subPageRoles')}
                      onCancel={() => {
                        reset(application);
                        setFormReadonly(true);
                      }}
                      onDelete={() => {
                      }}
                      canBeEdited={mayEditApplication(account, application)}
                      readonly={formReadonly}
                      onStartEditing={() => setFormReadonly(false)}
                      hideDialogControls={true}
          />
          <Grid container={true} sx={{padding: theme.spacing(1), width: '100%'}}>
            {!translationsOfRolesComplete(application) && application.id &&
              <Grid item={true} xs={12} style={{marginTop: 16, marginBottom: 16}}>
                <Typography color={"error"} variant={"body2"}>{t('Application.translationsMissingMessage')}</Typography>
              </Grid>
            }
            <Grid item={true} xs={12} lg={12}>
              <Grid container={true} spacing={3}>
                <Grid item={true} xs={12} lg={6} sx={{marginTop: theme.spacing(1)}}>
                  <MuiRhfSelectField name={'adminRole'}
                                     list={rolesWatch.map(role => role.name)}
                                     label={t('Application.adminRole')}
                                     fullWidth={true}
                                     required={required?.adminRole}
                                     {...stdFieldProps} />
                </Grid>
                <Grid item={true} xs={12} sx={{marginTop: theme.spacing(1)}}>
                  <ApplicationRolesComponent disabled={formReadonly} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {!formReadonly &&
            <Typography variant={"caption"}>{t('Application.updateMessage')}</Typography>}
        </RhfForm>
      </FormProvider>
    </ApplicationSubPageContentContainer>
  )
}
